import { useState, type Dispatch, type FormEvent, type ReactNode, type SetStateAction } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ban, Download, Eye, Paperclip, Plus, Receipt } from 'lucide-react';
import {
  EXPENSE_FREQUENCIES,
  EXPENSE_FREQUENCY_LABELS,
  EXPENSE_STATUSES,
  EXPENSE_STATUS_LABELS,
  type ExpenseDetail,
  type ExpenseFrequency,
  type ExpenseStatus,
  PAYMENT_METHODS,
  PAYMENT_METHOD_LABELS,
  type Expense,
  type ExpenseCategory,
  type Paginated,
  type PaymentMethod,
  type Staff,
} from '@cenox/shared';
import { ApiError, api, downloadFile, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useLocationFilter, useLocationReady, useRecordLocation } from '../../lib/locationContext';
import { LocationField } from '../../components/LocationField';
import { date, money, toDateTimeLocal, todayIn } from '../../lib/format';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  Pagination,
  Select,
  TableSkeleton,
  Textarea,
} from '../../components/ui';
import { PageHeader } from '../../layouts/AppShell';
import { RecurringExpenses } from './RecurringExpenses';
import { ExpenseSummaryPanel } from './ExpenseSummary';
import { AccountSelect } from '../money/AccountSelect';
import { useUploadEvidence } from '../money/data';

const STATUS_TONES: Record<ExpenseStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  pending: 'warning',
  partial: 'info',
  paid: 'success',
  cancelled: 'neutral',
};

/** Where it stands. Older payloads carry only the payment state. */
function statusOf(expense: Expense): ExpenseStatus {
  return expense.status ?? (expense.paymentState === 'unpaid' ? 'pending' : expense.paymentState === 'partial' ? 'partial' : 'paid');
}

function StatusBadge({ expense }: { expense: Expense }): ReactNode {
  const status = statusOf(expense);
  return (
    <span className="inline-flex flex-wrap justify-end gap-1">
      <Badge tone={STATUS_TONES[status]}>{EXPENSE_STATUS_LABELS[status]}</Badge>
      {expense.testEntry ? <Badge tone="neutral">Test entry</Badge> : null}
    </span>
  );
}

const payable = (expense: Expense) => statusOf(expense) === 'pending' || statusOf(expense) === 'partial';

export function ExpensesPage(): ReactNode {
  const { business, can } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState<ExpenseStatus | ''>('');
  const [creating, setCreating] = useState(false);

  // The working location narrows the list; "all locations" sends nothing,
  // which is what "do not narrow" means to the API.
  const locationFilter = useLocationFilter();
  const locationReady = useLocationReady();

  const filters = qs({
    page,
    pageSize: 25,
    search: search || undefined,
    categoryId: categoryId || undefined,
    status: status || undefined,
    ...locationFilter,
  });

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['expenses', business?.businessId, filters],
    queryFn: () => api.get<Paginated<Expense>>(path(`/expenses${filters}`)),
    // Wait for the working location, or the first request asks for the whole
    // business and the user watches the wrong answer go past.
    enabled: Boolean(business) && locationReady,
  });

  const { data: categories } = useQuery({
    queryKey: ['expense-categories', business?.businessId],
    queryFn: () => api.get<{ data: ExpenseCategory[] }>(path('/expense-categories')),
    enabled: Boolean(business),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['expenses'] });
    void queryClient.invalidateQueries({ queryKey: ['expense-categories'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['reports'] });
  };

  const [paying, setPaying] = useState<Expense | null>(null);
  const [cancelling, setCancelling] = useState<Expense | null>(null);
  const [viewing, setViewing] = useState<Expense | null>(null);
  const filtered = Boolean(search || categoryId || status);

  const timezone = business?.timezone ?? 'UTC';

  return (
    <>
      <PageHeader
        title="Expenses"
        description="What the business spends, and on what"
        action={
          <div className="flex gap-2">
            {can('reports:export') && (
              <Button
                variant="secondary"
                icon={<Download className="size-4" />}
                onClick={() =>
                  void downloadFile(
                    `/api/v1${path(`/exports${qs({ dataset: 'expenses', format: 'xlsx', ...locationFilter })}`)}`,
                    'expenses.xlsx',
                  )
                }
              >
                <span className="hidden sm:inline">Export</span>
              </Button>
            )}
            {can('expenses:create') && (
              <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                Record expense
              </Button>
            )}
          </div>
        }
      />

      {/*
        Above the ledger, and visibly a different thing: these are costs that
        WILL be spent. Nothing here is in any total until it is recorded.
      */}
      <RecurringExpenses />

      <ExpenseSummaryPanel />

      <Card className="mb-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Input
            placeholder="Search description, vendor, reference, category…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            aria-label="Search expenses"
          />
          <Select
            placeholder="All categories"
            options={(categories?.data ?? []).map((category) => ({
              value: category.id,
              label: category.name,
            }))}
            value={categoryId}
            onChange={(event) => {
              setCategoryId(event.target.value);
              setPage(1);
            }}
            aria-label="Filter by category"
          />
          <Select
            placeholder="Every expense that stands"
            options={EXPENSE_STATUSES.map((value) => ({ value, label: EXPENSE_STATUS_LABELS[value] }))}
            value={status}
            onChange={(event) => {
              setStatus(event.target.value as ExpenseStatus | '');
              setPage(1);
            }}
            aria-label="Filter by status"
          />
        </div>
      </Card>

      <Card padded={false}>
        {isPending ? (
          <TableSkeleton columns={5} />
        ) : error ? (
          <div className="p-4">
            <ErrorState message="Could not load expenses." onRetry={() => refetch()} />
          </div>
        ) : data.data.length === 0 ? (
          <EmptyState
            icon={<Receipt className="size-10" />}
            title={filtered ? 'No expenses match your filters' : 'No expenses yet'}
            description={
              filtered
                ? 'Try clearing a filter.'
                : 'Record what you spend to see real profit, not just revenue.'
            }
            action={
              can('expenses:create') && !filtered ? (
                <Button icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>
                  Record expense
                </Button>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50">
                  <tr>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Reference</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Date</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Category</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Description</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Vendor</th>
                    <th scope="col" className="px-4 py-2.5 text-left text-xs font-medium text-slate-600">Status</th>
                    <th scope="col" className="px-4 py-2.5 text-right text-xs font-medium text-slate-600">Amount</th>
                    <th scope="col" className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.data.map((expense) => (
                    <tr key={expense.id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-mono text-xs text-slate-500">
                        {expense.reference}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">{date(expense.incurredAt, timezone)}</td>
                      <td className="px-4 py-3">
                        {expense.categoryName ? (
                          <Badge>{expense.categoryName}</Badge>
                        ) : (
                          <span className="text-slate-400">Uncategorised</span>
                        )}
                      </td>
                      <td className="max-w-xs truncate px-4 py-3">{expense.description ?? '—'}</td>
                      <td className="max-w-[10rem] truncate px-4 py-3 text-slate-600">
                        {expense.vendor ?? '—'}
                      </td>
                      <td className="px-4 py-3"><StatusBadge expense={expense} /></td>
                      <td className="px-4 py-3 text-right font-medium tabular text-red-700">
                        {money(expense.amount, expense.currencyCode)}
                        {statusOf(expense) === 'partial' ? (
                          <span className="block text-xs font-normal text-slate-500">
                            {money(expense.outstanding, expense.currencyCode)} to pay
                          </span>
                        ) : null}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-right">
                        <RowActions
                          expense={expense}
                          onPay={() => setPaying(expense)}
                          onView={() => setViewing(expense)}
                          onCancel={() => setCancelling(expense)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="divide-y divide-slate-100 md:hidden">
              {data.data.map((expense) => (
                <li key={expense.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-slate-900">
                        {expense.description ?? expense.reference}
                      </p>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {date(expense.incurredAt, timezone)}
                        {expense.vendor && ` · ${expense.vendor}`}
                      </p>
                    </div>
                    <p className="shrink-0 font-semibold tabular text-red-700">
                      {money(expense.amount, expense.currencyCode)}
                    </p>
                  </div>
                  <div className="mt-2.5 flex flex-wrap items-center gap-2">
                    <StatusBadge expense={expense} />
                    {expense.categoryName && <Badge>{expense.categoryName}</Badge>}
                    <div className="ml-auto">
                      <RowActions
                        expense={expense}
                        onPay={() => setPaying(expense)}
                        onView={() => setViewing(expense)}
                        onCancel={() => setCancelling(expense)}
                      />
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            <Pagination
              page={data.meta.page}
              pageSize={data.meta.pageSize}
              total={data.meta.total}
              onPageChange={setPage}
            />
          </>
        )}
      </Card>

      {paying ? <RecordPaymentModal expense={paying} onClose={() => setPaying(null)} /> : null}
      {cancelling ? (
        <CancelExpenseModal
          expense={cancelling}
          onClose={() => setCancelling(null)}
          onDone={() => {
            setCancelling(null);
            invalidate();
          }}
        />
      ) : null}
      {viewing ? <ExpenseDetailModal expense={viewing} onClose={() => setViewing(null)} /> : null}
      <Modal open={creating} onClose={() => setCreating(false)} title="Record an expense" size="lg">
        <ExpenseForm
          categories={categories?.data ?? []}
          onDone={() => {
            setCreating(false);
            invalidate();
          }}
          onCancel={() => setCreating(false)}
        />
      </Modal>
    </>
  );
}

// ── Form ────────────────────────────────────────────────────────────────────

function ExpenseForm({
  categories,
  onDone,
  onCancel,
}: {
  categories: ExpenseCategory[];
  onDone: () => void;
  onCancel: () => void;
}): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const timezone = business?.timezone ?? 'UTC';

  const today = todayIn(timezone);
  const [form, setForm] = useState<ExpenseForm>({
    /**
     * One-off, or an arrangement that repeats.
     *
     * The whole shape of what gets written turns on this: a one-time payment
     * is an expense, and a recurring one is a SCHEDULE that produces expenses
     * as they are taken. Nothing future is ever written to the ledger.
     */
    type: 'one_time' as 'one_time' | 'recurring',
    amount: '',
    categoryId: '',
    newCategory: '',
    description: '',
    paymentMethod: 'cash' as PaymentMethod,
    incurredAt: toDateTimeLocal(new Date().toISOString(), timezone),
    notes: '',
    vendor: '',
    staffId: '',
    paid: true,
    // Which account it was paid from; empty is the location's default for the method.
    accountId: '',

    frequency: 'monthly' as ExpenseFrequency,
    intervalDays: '30',
    startsOn: today,
    nextDueOn: today,
    endsOn: '',
    noEndDate: true,
    autoCreate: false,
    reminderDays: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  // Money was spent somewhere. The API refuses an expense that does not say where.
  const location = useRecordLocation();
  const { can } = useAuth();
  const upload = useUploadEvidence();
  const [receipt, setReceipt] = useState<File | null>(null);
  const { data: staff } = useQuery({
    queryKey: ['staff', 'for-expenses', business?.businessId],
    queryFn: () => api.get<Paginated<Staff>>(path('/staff?pageSize=100')),
    enabled: Boolean(business) && can('staff:read'),
    staleTime: 60_000,
  });

  const update =
    <K extends keyof typeof form>(key: K) =>
    (event: { target: { value: string } }) =>
      setForm((current) => ({ ...current, [key]: event.target.value }));

  const create = useMutation({
    mutationFn: ({ recurring, ...payload }: Record<string, unknown> & { recurring: boolean }) =>
      api.post(path(recurring ? '/expense-schedules' : '/expenses'), payload),
    onSuccess: onDone,
    onError: (caught) => {
      if (caught instanceof ApiError) {
        setError(caught.message);
        if (caught.fields) {
          setFieldErrors(
            Object.fromEntries(
              Object.entries(caught.fields).map(([key, messages]) => [key, messages[0] ?? '']),
            ),
          );
        }
      } else {
        setError('Could not record the expense.');
      }
    },
  });

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    if (!location.branchId) {
      setFieldErrors({ branchId: 'Choose which location this expense belongs to.' });
      return;
    }

    let attachmentId: string | undefined;
    if (receipt) {
      try {
        attachmentId = (await upload.mutateAsync(receipt)).id;
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'Could not upload the receipt.');
        return;
      }
    }

    const shared = {
      branchId: location.branchId,
      amount: form.amount,
      // A typed name creates the category server-side; an id reuses one.
      ...(form.categoryId === '__new'
        ? { categoryName: form.newCategory }
        : { categoryId: form.categoryId || null }),
      description: form.description || undefined,
      paymentMethod: form.paymentMethod,
      notes: form.notes || undefined,
    };
    const paidFrom = form.type === 'one_time' && form.paid && form.accountId ? { accountId: form.accountId } : {};

    if (form.type === 'one_time') {
      create.mutate({
        recurring: false,
        ...shared,
        incurredAt: new Date(form.incurredAt).toISOString(),
        ...(form.paid ? {} : { paid: false }),
        ...paidFrom,
        ...(form.vendor.trim() ? { vendor: form.vendor.trim() } : {}),
        ...(form.staffId ? { staffId: form.staffId } : {}),
        ...(attachmentId ? { attachmentId } : {}),
      });
      return;
    }

    create.mutate({
      recurring: true,
      ...shared,
      frequency: form.frequency,
      ...(form.frequency === 'custom' ? { intervalDays: Number(form.intervalDays) } : {}),
      startsOn: form.startsOn,
      nextDueOn: form.nextDueOn || form.startsOn,
      // "No end date" is the common case, and it is a null rather than a far
      // future date — a schedule that ends in 2099 is still a schedule that ends.
      endsOn: form.noEndDate ? null : form.endsOn || null,
      autoCreate: form.autoCreate,
      reminderDays: form.reminderDays === '' ? null : Number(form.reminderDays),
    });
  }

  const currency = business?.currencyCode ?? '';

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4" noValidate>
      {error && !Object.keys(fieldErrors).length && (
        <div role="alert" className="rounded-lg border border-red-200 bg-red-50 px-3 py-2.5">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <LocationField
        value={location.branchId}
        onChange={location.setBranchId}
        locations={location.locations}
        error={fieldErrors['branchId']}
      />

      {/*
        First, because it decides what the rest of the form means. A one-time
        payment writes an expense; a recurring one writes a SCHEDULE, and no
        future amount ever reaches the ledger or a report.
      */}
      <Select
        label="Type"
        required
        options={[
          { value: 'one_time', label: 'One-time payment' },
          { value: 'recurring', label: 'Recurring' },
        ]}
        value={form.type}
        onChange={update('type')}
        hint={
          form.type === 'recurring'
            ? 'Sets up a schedule. Each payment is recorded when it is actually made.'
            : undefined
        }
      />

      <Input
        label={`Amount (${currency})`}
        required
        autoFocus
        inputMode="decimal"
        placeholder="0.00"
        value={form.amount}
        onChange={update('amount')}
        error={fieldErrors.amount}
      />

      <Select
        label="Category"
        placeholder="Uncategorised"
        options={[
          ...categories.map((category) => ({ value: category.id, label: category.name })),
          { value: '__new', label: '+ New category…' },
        ]}
        value={form.categoryId}
        onChange={update('categoryId')}
        error={fieldErrors.categoryId}
      />

      {form.categoryId === '__new' && (
        <Input
          label="New category name"
          required
          value={form.newCategory}
          onChange={update('newCategory')}
          placeholder="e.g. Generator Diesel"
        />
      )}

      <Input
        label="Description"
        value={form.description}
        onChange={update('description')}
        error={fieldErrors.description}
        placeholder="What was this for?"
      />

      {form.type === 'one_time' ? (
      <Input
        label="Vendor / paid to"
        value={form.vendor}
        onChange={update('vendor')}
        error={fieldErrors.vendor}
        placeholder="e.g. Landlord, IKEDC, the repairer"
      />
      ) : null}

      {form.type === 'one_time' && staff && staff.data.length > 0 ? (
        <Select
          label="Staff member responsible"
          placeholder="Nobody in particular"
          options={staff.data.map((member) => ({ value: member.id, label: member.fullName }))}
          value={form.staffId}
          onChange={update('staffId')}
          error={fieldErrors.staffId}
        />
      ) : null}

      <Select
        label="Payment method"
        required
        options={PAYMENT_METHODS.map((value) => ({
          value,
          label: PAYMENT_METHOD_LABELS[value],
        }))}
        value={form.paymentMethod}
        onChange={(e) => setForm((current) => ({ ...current, paymentMethod: e.target.value as PaymentMethod, accountId: '' }))}
        error={fieldErrors.paymentMethod}
      />
      {form.type === 'one_time' && form.paid ? (
        <AccountSelect
          method={form.paymentMethod}
          value={form.accountId}
          onChange={(accountId) => setForm((current) => ({ ...current, accountId }))}
          error={fieldErrors.accountId}
        />
      ) : null}

      {form.type === 'one_time' ? (
        <>
          <Input
            label="Date and time"
            type="datetime-local"
            value={form.incurredAt}
            onChange={update('incurredAt')}
            error={fieldErrors.incurredAt}
          />
          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              className="mt-0.5 size-4 rounded border-slate-300"
              checked={form.paid}
              onChange={(e) => setForm((current) => ({ ...current, paid: e.target.checked }))}
            />
            <span>
              Paid already
              <span className="mt-0.5 block text-xs text-slate-500">
                Untick for a bill not paid yet: it counts as a cost from its date, and money leaves Cash &amp; bank
                only as payments are recorded — in full or in parts.
              </span>
            </span>
          </label>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700" htmlFor="expense-receipt">
              Receipt or invoice
            </label>
            <div className="flex items-center gap-2">
              <input
                id="expense-receipt"
                type="file"
                accept="image/*,application/pdf"
                className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium"
                onChange={(e) => setReceipt(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
        </>
      ) : (
        <RecurringFields form={form} setForm={setForm} update={update} errors={fieldErrors} />
      )}

      <Textarea label="Notes" value={form.notes} onChange={update('notes')} />

      <div className="flex justify-end gap-2 pt-1">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" loading={create.isPending || upload.isPending}>
          {form.type === 'recurring' ? 'Set up recurring expense' : 'Record expense'}
        </Button>
      </div>
    </form>
  );
}

/**
 * Everything the entry form holds.
 *
 * One shape for both types, with the recurrence fields unused while the type is
 * `one_time`. Two shapes would mean a discriminated union and a cast at every
 * `update()` call, for a form that is a dozen fields long.
 */
interface ExpenseForm {
  type: 'one_time' | 'recurring';
  amount: string;
  categoryId: string;
  newCategory: string;
  description: string;
  paymentMethod: PaymentMethod;
  incurredAt: string;
  notes: string;
  vendor: string;
  staffId: string;
  paid: boolean;
  accountId: string;
  frequency: ExpenseFrequency;
  intervalDays: string;
  startsOn: string;
  nextDueOn: string;
  endsOn: string;
  noEndDate: boolean;
  autoCreate: boolean;
  reminderDays: string;
}

/**
 * The fields a recurring expense needs, and only it.
 *
 * Revealed by the Type selector rather than always on screen: a one-time
 * payment is most of what anybody records, and five extra controls above the
 * Save button is the fastest way to make a simple form feel like paperwork.
 */
function RecurringFields({
  form,
  setForm,
  update,
  errors,
}: {
  form: ExpenseForm;
  setForm: Dispatch<SetStateAction<ExpenseForm>>;
  update: (key: keyof ExpenseForm) => (event: { target: { value: string } }) => void;
  errors: Record<string, string>;
}): ReactNode {
  const set = (patch: Partial<ExpenseForm>) =>
    setForm((current) => ({ ...current, ...patch }));

  return (
    <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Select
          label="Frequency"
          required
          options={EXPENSE_FREQUENCIES.map((value) => ({
            value,
            label: EXPENSE_FREQUENCY_LABELS[value],
          }))}
          value={form.frequency}
          onChange={update('frequency')}
          error={errors.frequency}
        />

        {form.frequency === 'custom' && (
          <Input
            label="Every (days)"
            required
            type="number"
            min={1}
            max={365}
            value={form.intervalDays}
            onChange={update('intervalDays')}
            error={errors.intervalDays}
            hint="For arrangements a calendar word does not fit"
          />
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          label="Start date"
          required
          type="date"
          value={form.startsOn}
          onChange={(e) => {
            // The start anchors the recurrence, so moving it moves the first
            // due date with it unless the user has already set one themselves.
            const value = e.target.value;
            set(form.nextDueOn === form.startsOn ? { startsOn: value, nextDueOn: value } : { startsOn: value });
          }}
          error={errors.startsOn}
          hint="Month-end schedules stay anchored: the 31st becomes the 28th in February, then returns"
        />

        <Input
          label="Next due date"
          required
          type="date"
          value={form.nextDueOn}
          onChange={update('nextDueOn')}
          error={errors.nextDueOn}
          hint="For an arrangement already running, so it does not try to catch up"
        />
      </div>

      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="size-4 rounded border-slate-300"
            checked={form.noEndDate}
            onChange={(e) => set({ noEndDate: e.target.checked, ...(e.target.checked ? { endsOn: '' } : {}) })}
          />
          No end date
        </label>

        {!form.noEndDate && (
          <Input
            label="End date"
            type="date"
            value={form.endsOn}
            onChange={update('endsOn')}
            error={errors.endsOn}
          />
        )}
      </div>

      <label className="flex items-start gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          className="mt-0.5 size-4 rounded border-slate-300"
          checked={form.autoCreate}
          onChange={(e) => set({ autoCreate: e.target.checked })}
        />
        <span>
          Record it automatically when it falls due
          <span className="mt-0.5 block text-xs text-slate-500">
            Off by default. Left off, each payment waits for somebody to confirm it — which is
            the point of having the list.
          </span>
        </span>
      </label>

      <Input
        label="Remind me (days before)"
        type="number"
        min={0}
        max={90}
        value={form.reminderDays}
        onChange={update('reminderDays')}
        error={errors.reminderDays}
        hint="Optional. Leave blank for no reminder."
      />
    </div>
  );
}

function RowActions({
  expense,
  onPay,
  onView,
  onCancel,
}: {
  expense: Expense;
  onPay: () => void;
  onView: () => void;
  onCancel: () => void;
}): ReactNode {
  const { can } = useAuth();
  const cancelled = statusOf(expense) === 'cancelled';
  return (
    <span className="inline-flex items-center gap-1">
      {payable(expense) && can('expenses:create') ? (
        <Button variant="secondary" size="sm" onClick={onPay}>Record payment</Button>
      ) : null}
      <Button variant="ghost" size="sm" icon={<Eye className="size-4" />} aria-label={`Details of ${expense.reference}`} onClick={onView} />
      {!cancelled && can('expenses:delete') ? (
        <Button variant="ghost" size="sm" icon={<Ban className="size-4" />} aria-label={`Cancel expense ${expense.reference}`} onClick={onCancel} />
      ) : null}
    </span>
  );
}

/**
 * Record a payment towards an expense not fully paid: all that is owed, or
 * part of it. Cash & Bank takes it out of the account on the day given here,
 * from the account chosen — not on the day the cost was due.
 */
function RecordPaymentModal({ expense, onClose }: { expense: Expense; onClose: () => void }): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const today = todayIn(business?.timezone ?? 'UTC');
  // Older payloads have no `outstanding`: then all of it is owed.
  const owed = expense.outstanding ?? expense.amount;
  const [paidOn, setPaidOn] = useState(today);
  const [amount, setAmount] = useState(owed);
  const [method, setMethod] = useState<PaymentMethod>(expense.paymentMethod === 'credit' ? 'cash' : expense.paymentMethod);
  const [accountId, setAccountId] = useState(expense.accountId ?? '');
  const [reference, setReference] = useState('');
  const part = amount.trim() !== '' && Number(amount) !== Number(owed);
  const pay = useMutation({
    mutationFn: () =>
      api.post(path(`/expenses/${expense.id}/payment`), {
        paidOn,
        // Left out, the API pays what is still owed: only a part is sent as an amount.
        ...(part ? { amount: amount.trim() } : {}),
        paymentMethod: method,
        accountId: accountId || null,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['expenses'] });
      await queryClient.invalidateQueries({ queryKey: ['money'] });
      onClose();
    },
  });
  return (
    <Modal open onClose={onClose} title={`Record payment — ${expense.reference}`}>
      <form
        className="space-y-3"
        aria-label="Record payment"
        onSubmit={(e) => {
          e.preventDefault();
          pay.mutate();
        }}
      >
        <p className="text-sm text-slate-600">
          {money(owed, expense.currencyCode)} still to pay
          {Number(owed) !== Number(expense.amount) ? ` of ${money(expense.amount, expense.currencyCode)}` : ''} for{' '}
          {expense.description ?? 'this expense'}, due {expense.occurrenceOn ?? expense.incurredAt.slice(0, 10)}. Cash &amp;
          Bank takes it out of the account on the day you enter here, not on the due date.
        </p>
        <Input
          label={`Amount paid (${expense.currencyCode})`}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          hint={part ? 'A part payment: the rest stays to pay.' : 'Pays all that is owed.'}
        />
        <Input label="Paid on" type="date" max={today} value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        <Select
          label="Paid by"
          value={method}
          onChange={(e) => setMethod(e.target.value as PaymentMethod)}
          options={PAYMENT_METHODS.filter((m) => m !== 'credit').map((m) => ({ value: m, label: PAYMENT_METHOD_LABELS[m] }))}
        />
        <AccountSelect method={method} value={accountId} onChange={setAccountId} />
        <Input label="Reference" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Transfer or receipt number" />
        {pay.error ? <p role="alert" className="text-sm text-red-700">{pay.error instanceof ApiError ? pay.error.message : 'Could not record it.'}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" loading={pay.isPending}>Record payment</Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Cancel an expense, saying why. It leaves every total; anything already paid
 * on it comes back to its account today — the days it left are not rewritten.
 */
function CancelExpenseModal({ expense, onClose, onDone }: { expense: Expense; onClose: () => void; onDone: () => void }): ReactNode {
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const [reason, setReason] = useState('');
  const paidOnIt = expense.amountPaid ?? (expense.paymentState === 'paid' ? expense.amount : '0');
  const cancel = useMutation({
    mutationFn: () => api.post(path(`/expenses/${expense.id}/cancel`), { reason: reason.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['money'] });
      onDone();
    },
  });
  return (
    <Modal open onClose={onClose} title={`Cancel ${expense.reference}`}>
      <form
        className="space-y-3"
        aria-label="Cancel expense"
        onSubmit={(e) => {
          e.preventDefault();
          if (reason.trim()) cancel.mutate();
        }}
      >
        <p className="text-sm text-slate-600">
          {money(expense.amount, expense.currencyCode)} for {expense.description ?? 'this expense'} stops counting as a cost.
          {Number(paidOnIt) > 0
            ? ` The ${money(paidOnIt, expense.currencyCode)} already paid on it is returned to its account today; the days it was paid are not changed.`
            : ' Nothing was paid on it, so no money moves.'}{' '}
          The record and its history are kept.
        </p>
        <Input label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Entered twice; bill withdrawn" />
        {cancel.error ? <p role="alert" className="text-sm text-red-700">{cancel.error instanceof ApiError ? cancel.error.message : 'Could not cancel it.'}</p> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Keep it</Button>
          <Button type="submit" variant="danger" disabled={!reason.trim()} loading={cancel.isPending}>Cancel expense</Button>
        </div>
      </form>
    </Modal>
  );
}

/** One expense: its payments, its receipt, and why it was cancelled if it was. */
function ExpenseDetailModal({ expense, onClose }: { expense: Expense; onClose: () => void }): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const timezone = business?.timezone ?? 'UTC';
  const { data, isPending, error } = useQuery({
    queryKey: ['expenses', 'detail', expense.id],
    queryFn: () => api.get<ExpenseDetail>(path(`/expenses/${expense.id}`)),
  });
  const e = data ?? expense;
  const row = (label: string, value: ReactNode) => (
    <div className="flex justify-between gap-4 py-1.5 text-sm">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right text-slate-900">{value}</dd>
    </div>
  );
  return (
    <Modal open onClose={onClose} title={`Expense ${expense.reference}`} size="lg">
      <dl className="divide-y divide-slate-100">
        {row('Status', <StatusBadge expense={e} />)}
        {row('Amount', money(e.amount, e.currencyCode))}
        {row('Paid so far', money(e.amountPaid ?? '0', e.currencyCode))}
        {row('Still to pay', money(e.outstanding ?? '0', e.currencyCode))}
        {row('Date', date(e.incurredAt, timezone))}
        {row('Category', e.categoryName ?? 'Uncategorised')}
        {row('Vendor / paid to', e.vendor ?? '—')}
        {row('Location', e.branchName ?? '—')}
        {row('Staff member responsible', e.staffName ?? '—')}
        {row('Recorded by', e.recordedByName ?? 'Automatically, from a schedule')}
        {e.description ? row('Description', e.description) : null}
        {e.notes ? row('Notes', e.notes) : null}
        {e.attachmentUrl
          ? row(
              'Receipt',
              <a className="inline-flex items-center gap-1 text-brand-700 underline" href={e.attachmentUrl} target="_blank" rel="noreferrer">
                <Paperclip className="size-3.5" /> Open
              </a>,
            )
          : null}
        {e.cancelledAt ? row('Cancelled', `${date(e.cancelledAt, timezone)}${e.cancelReason ? ` — ${e.cancelReason}` : ''}`) : null}
        {e.testEntry
          ? row('Test entry', `${date(e.testEntry.at, timezone)}${e.testEntry.byName ? `, ${e.testEntry.byName}` : ''} — ${e.testEntry.reason}`)
          : null}
      </dl>
      {e.cancelledAt ? <TestEntryControl expense={e} /> : null}

      <h3 className="mt-5 mb-2 text-sm font-semibold text-slate-900">Payments</h3>
      {isPending ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : error ? (
        <p className="text-sm text-red-700">Could not load the payments.</p>
      ) : data.payments.length === 0 ? (
        <p className="text-sm text-slate-500">
          {statusOf(e) === 'paid'
            ? `Paid in full${e.paidAt ? ` on ${date(e.paidAt, timezone)}` : ''} by ${PAYMENT_METHOD_LABELS[e.paymentMethod]}.`
            : 'Nothing paid yet.'}
        </p>
      ) : (
        <table className="w-full text-sm" aria-label="Payments">
          <thead className="border-b border-slate-200 text-xs text-slate-600">
            <tr>
              <th scope="col" className="py-1.5 text-left font-medium">Paid on</th>
              <th scope="col" className="py-1.5 text-left font-medium">How</th>
              <th scope="col" className="py-1.5 text-left font-medium">Reference</th>
              <th scope="col" className="py-1.5 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.payments.map((p) => (
              <tr key={p.id}>
                <td className="py-1.5">{date(p.paidAt, timezone)}</td>
                <td className="py-1.5">{PAYMENT_METHOD_LABELS[p.paymentMethod]}</td>
                <td className="py-1.5 text-slate-600">{p.reference ?? '—'}</td>
                <td className="py-1.5 text-right tabular">{money(p.amount, e.currencyCode)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="mt-4 text-xs text-slate-500">Every change to this expense is kept in the audit log.</p>
    </Modal>
  );
}

/**
 * Mark a cancelled expense as test data, or undo it (0164). The owner's alone.
 *
 * A cancelled paid expense still shows its payment and its return in Cash &
 * bank, because real money normally moved both ways. When it was only ever a
 * test, nothing moved: marked, neither shows anywhere. The record, its
 * cancellation and its audit history stay.
 */
function TestEntryControl({ expense }: { expense: Expense }): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const marked = Boolean(expense.testEntry);
  const mark = useMutation({
    mutationFn: () => api.post(path(`/expenses/${expense.id}/test-entry`), { testEntry: !marked, reason: reason.trim() }),
    onSuccess: async () => {
      setOpen(false);
      setReason('');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['expenses'] }),
        queryClient.invalidateQueries({ queryKey: ['money'] }),
      ]);
    },
  });
  if (business?.role !== 'owner') return null;
  if (!open) {
    return (
      <div className="mt-3">
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
          {marked ? 'Undo test-entry mark' : 'Mark as test entry'}
        </Button>
      </div>
    );
  }
  return (
    <form
      className="mt-3 space-y-2 rounded-lg border border-slate-200 p-3"
      aria-label={marked ? 'Undo test entry' : 'Mark as test entry'}
      onSubmit={(event) => {
        event.preventDefault();
        mark.mutate();
      }}
    >
      <p className="text-sm text-slate-600">
        {marked
          ? 'Its payment and return will count in Cash & bank and the expense summary again.'
          : 'Only if no real money was paid or returned. Its payment and return stop counting in Cash & bank and the expense summary, on every day. The record, its cancellation and its history are kept, marked as test data.'}
      </p>
      <Input label="Reason" required value={reason} onChange={(event) => setReason(event.target.value)} placeholder="e.g. Created to try the screen; no money moved" />
      {mark.error ? <p role="alert" className="text-sm text-red-700">{mark.error instanceof ApiError ? mark.error.message : 'Could not save it.'}</p> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Back</Button>
        <Button type="submit" disabled={reason.trim().length < 3} loading={mark.isPending}>
          {marked ? 'Undo mark' : 'Mark as test entry'}
        </Button>
      </div>
    </form>
  );
}
